import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  MOBILE_AUTH,
  REQUEST_STATUS,
  mobileInitialNotifications,
  mobileInitialRequests,
  mobileSummary,
} from './mobileMockData';

// يحسب دقائق التأخر عن وقت بدء الدوام بناءً على الوقت الحالي. يرجع 0 إن لم يتأخر.
const computeLateMinutes = (shiftStart) => {
  if (!shiftStart) return 0;
  const [h, m] = shiftStart.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  const now = new Date();
  const start = new Date(now);
  start.setHours(h, m, 0, 0);
  const diffMs = now.getTime() - start.getTime();
  return diffMs > 0 ? Math.floor(diffMs / 60000) : 0;
};

const MOBILE_AUTH_STORAGE_KEY = 'mobileAttendanceLoggedIn';

const readInitialAuth = () => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  return localStorage.getItem(MOBILE_AUTH_STORAGE_KEY) === '1';
};

const MobileAppContext = createContext(null);

let requestSeq = 1000;

const formatPunchTime = () =>
  new Intl.DateTimeFormat('ar-EG', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date());

export const MobileAppProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(readInitialAuth);
  const [requests, setRequests] = useState(mobileInitialRequests);
  const [notifications, setNotifications] = useState(mobileInitialNotifications);
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [lastPunch, setLastPunch] = useState(null);
  // دقائق التأخر تُحسب مرة واحدة عند فتح التطبيق.
  const [lateMinutes] = useState(() => computeLateMinutes(mobileSummary.shiftStart));

  const setLoggedIn = useCallback((value) => {
    setIsLoggedIn(value);
    if (typeof window !== 'undefined' && window.localStorage) {
      if (value) {
        localStorage.setItem(MOBILE_AUTH_STORAGE_KEY, '1');
      } else {
        localStorage.removeItem(MOBILE_AUTH_STORAGE_KEY);
      }
    }
  }, []);

  const login = useCallback((email, password) => {
    const ok =
      String(email).trim().toLowerCase() === MOBILE_AUTH.email &&
      String(password) === MOBILE_AUTH.password;
    if (ok) {
      setLoggedIn(true);
    }
    return ok;
  }, [setLoggedIn]);

  const logout = useCallback(() => {
    setIsLoggedIn(false);
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(MOBILE_AUTH_STORAGE_KEY);
    }
  }, []);

  const addRequest = useCallback((request) => {
    requestSeq += 1;
    const newRequest = {
      idKey: `req-${requestSeq}`,
      status: REQUEST_STATUS.PENDING,
      sentAt: new Intl.DateTimeFormat('ar-EG', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
      permitDate: request.startDate || '-',
      time: 'يوم كامل',
      rejectReason: null,
      approvedBy: null,
      ...request,
    };
    setRequests((prev) => [newRequest, ...prev]);
    return newRequest;
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
  }, []);

  const checkIn = useCallback(() => {
    setIsCheckedIn(true);
    setLastPunch({ type: 'in', time: formatPunchTime() });
  }, []);

  const checkOut = useCallback(() => {
    setIsCheckedIn(false);
    setLastPunch({ type: 'out', time: formatPunchTime() });
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.read).length,
    [notifications]
  );

  const value = useMemo(
    () => ({
      isLoggedIn,
      login,
      logout,
      setLoggedIn,
      requests,
      setRequests,
      addRequest,
      notifications,
      unreadCount,
      markAllNotificationsRead,
      isCheckedIn,
      lastPunch,
      checkIn,
      checkOut,
      lateMinutes,
      isLate: lateMinutes > 0 && !isCheckedIn,
    }),
    [
      isLoggedIn,
      login,
      logout,
      setLoggedIn,
      requests,
      setRequests,
      addRequest,
      notifications,
      unreadCount,
      markAllNotificationsRead,
      isCheckedIn,
      lastPunch,
      checkIn,
      checkOut,
      lateMinutes,
    ]
  );

  return (
    <MobileAppContext.Provider value={value}>
      {children}
    </MobileAppContext.Provider>
  );
};

export const useMobileApp = () => {
  const ctx = useContext(MobileAppContext);
  if (!ctx) {
    throw new Error('useMobileApp must be used within MobileAppProvider');
  }
  return ctx;
};
